/* eslint-disable no-console */
import type { IncomingMessage } from "http";
import Cookies from "js-cookie";
import { ZEPHR_DECISIONS, type ZephrDecision } from "./constants";
import {
  DecisionEngineResponse,
  DecisionInputs,
  DecisionOutputs,
  SdkFeatures,
} from "./types";
import { ZEPHR_CDN_URL } from "lib/constants";

export const destroyZephrSession = async (): Promise<void> => {
  const session = Cookies.get("blaize_session");
  if (!session) {
    return;
  }

  try {
    await fetch("/blaize/logout", {
      method: "POST",
      body: JSON.stringify({
        where: "JUST_HERE",
      }),
      headers: {
        "Content-Type": "application/json",
        Cookie: `blaize_session=${session}`,
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error destroying session:", error);
  }
};

export const logoutAndReload = async (
  beforeReload?: () => Promise<void> | void
): Promise<void> => {
  await destroyZephrSession();

  try {
    await beforeReload?.();
  } finally {
    window.location.reload();
  }
};

export const makeDecision = async (
  decisionKey: ZephrDecision,
  inputs?: Record<string, unknown>
): Promise<unknown> => {
  try {
    if (typeof window === "undefined") {
      // eslint-disable-next-line no-console
      console.warn("Zephr decision called on server-side");
      return null;
    }

    const session = Cookies.get("blaize_session");
    const requestData = {
      sdkFeatureSlug: decisionKey,
      ...(session && { session }),
      ...inputs,
    };

    const response = await fetch("/zephr/decision-engine", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Zephr decision "${decisionKey}" failed`, {
      inputs,
      error,
    });

    return null;
  }
};

export const markOnboardingSeen = async (
  onboardingType: string
): Promise<unknown> => {
  return makeDecision(ZEPHR_DECISIONS.ONBOARDING_MARK_AS_SEEN, {
    "onboarding-type": onboardingType,
  });
};

export const makeDecisionServer = async (
  session: string,
  sdkFeatureSlug: SdkFeatures,
  input?: DecisionInputs,
  req?: IncomingMessage
): Promise<DecisionOutputs | undefined> => {
  const forwardedFor = req?.headers["x-forwarded-for"];
  const userAgent = req?.headers["user-agent"];

  const decisionEngineResponse = await fetch(
    ZEPHR_CDN_URL + "/zephr/decision-engine",
    {
      method: "POST",
      body: JSON.stringify({
        ...(input || {}),
        session,
        sdkFeatureSlug,
      }),
      headers: {
        "Content-Type": "application/json",
        ...(forwardedFor && {
          "X-Forwarded-For": Array.isArray(forwardedFor)
            ? forwardedFor[0]
            : forwardedFor,
        }),
        ...(userAgent && { "User-Agent": userAgent }),
      },
    }
  );

  if (decisionEngineResponse.status === 404) {
    console.error(
      `Decision engine endpoint not found for ${sdkFeatureSlug}. Please check the configuration.`
    );
  }

  if (!decisionEngineResponse.ok) {
    return undefined;
  }

  const responsePayload: DecisionEngineResponse =
    await decisionEngineResponse.json();

  const { outputValue } = responsePayload;
  if (outputValue === "") {
    return undefined;
  }

  try {
    return JSON.parse(outputValue);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      `Error parsing decision engine response for ${sdkFeatureSlug}:`,
      err
    );
    return undefined;
  }
};
